import React from 'react'
import moment from 'moment'
import { $DCprimaryColor, $DCWarningColor, DcChart, colors } from 'pixel-base'
import { formatDateForApi } from 'pixel-base'
import { getDashboardLandVsShip } from 'utils/api-routes-analytics'
import {
  axisPointerStyle,
  colorSquare,
  tooltipPosition,
  tooltipStyle,
  tooltipTrigger,
} from './chartStyles'

interface ShippingPerformanceProps {
  filters: {
    start_date: Date
    end_date: Date
    data_type: string
  }
  height?: string | number
  chartType?: 'bar' | 'line'
  setComponent: (_component: string) => void
  setShowDetail: (_showDetail: boolean) => void
  width?: string
}

const ShippingPerformance: React.FC<ShippingPerformanceProps> = ({
  filters,
  height = 350,
  chartType = 'line',
  setComponent,
  setShowDetail,
  width,
}: ShippingPerformanceProps) => {
  const [data, setData] = React.useState<any[]>([])
  const [loading, setLoading] = React.useState(true)

  React.useEffect(() => {
    setLoading(true)
    getDashboardLandVsShip({
      start_date: formatDateForApi(filters.start_date),
      end_date: formatDateForApi(filters.end_date),
      data_type: filters.data_type,
    }).subscribe((res: any) => {
      setData(res?.response?.data || [])
      setLoading(false)
    })
  }, [filters.start_date, filters.end_date, filters.data_type])

  const formatData = () => {
    if (!Array.isArray(data) || data.length === 0) {
      return {
        xAxis: [],
        series: [],
      }
    }

    const dates = data.map((item) => moment(item.date).format('DD Mmm'))
    const landData = data.map((item) => parseInt(item.landed_orders) || 0)
    const shipData = data.map((item) => parseInt(item.shipped_orders) || 0)

    return {
      xAxis: dates,
      series: [
        {
          name: 'Landed Orders',
          data: landData,
          itemStyle: { color: $DCprimaryColor },
        },
        {
          name: 'Shipped Orders',
          data: shipData,
          itemStyle: { color: $DCWarningColor },
        },
      ],
    }
  }

  const chartData = formatData()

  const chartOptions = {
    tooltip: {
      trigger: tooltipTrigger,
      axisPointer: axisPointerStyle,
      position: tooltipPosition,
      backgroundColor: tooltipStyle.backgroundColor,
      borderColor: tooltipStyle.borderColor,
      borderWidth: tooltipStyle.borderWidth,
      extraCssText: tooltipStyle.extraCssText,
      textStyle: tooltipStyle.textStyle,
      formatter: (params: any[]) => {
        if (!params || params.length === 0) return ''

        const date = params[0].axisValue
        let html = `
          <div style="margin-bottom: 8px;">
            <div style="font-size: 15px; font-weight: 600; margin-bottom: 4px;">${date}</div>
            <div style="height: 1px; background: ${colors.grayBorderLight}; margin: 8px 0;"></div>
          </div>
        `

        params.forEach((param) => {
          const value = param.value || 0
          const color = param.color
          const seriesName = param.seriesName

          html += `
            <div style="display: flex; justify-content: space-between; margin-bottom: 6px; align-items: center;">
              <div style="display: flex; align-items: center;">
                ${colorSquare(color)}${seriesName}:
              </div>&nbsp;&nbsp;
              <div style="font-weight: 500;">${value}</div>
            </div>
          `
        })

        return html
      },
    },
    legend: {
      orient: 'horizontal',
      top: 25,
      left: 'center',
      padding: [0, 10, 10, 10],
      textStyle: {
        fontSize: 9,
        color: colors.grayTextDark,
      },
    },
    grid: {
      top: 60,
      bottom: 30,
      left: 50,
      right: 20,
    },
    yAxis: {
      name: 'Count',
      nameTextStyle: {
        padding: [0, 0, 0, 40],
      },
    },
  }

  return (
    <DcChart
      title="Orders Land Vs Dispatch"
      isLoading={loading}
      isTitleClickable={true}
      onClickTitle={() => {
        setComponent('shippingPerformance')
        setShowDetail(true)
      }}
      type={chartType}
      data={chartData}
      options={chartOptions}
      height={height}
      data-testid="chart"
    />
  )
}

export default ShippingPerformance
